/**
 * TransfersPage - Transfers to UNE-Credito
 * Displays pending transfers, approval flow, and transfer history table.
 */

import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';

import { ROUTES } from '@/constants';
import { useTransfers } from '../../hooks';
import {
  PendingTransferCard,
  TransferHistoryTable,
  ApproveTransferModal,
} from '../../components';

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Cobranza', path: ROUTES.COLLECTION_DASHBOARD },
  { label: 'Transferencias', path: ROUTES.COLLECTION_TRANSFERS },
];

export function TransfersPage() {
  const {
    pendingTransfers,
    transferHistory,
    isApproveModalOpen,
    selectedTransferId,
    openApproveModal,
    closeApproveModal,
    confirmApprove,
    isApproving,
    isLoading,
    isError,
    refetchAll,
  } = useTransfers();

  // Find the selected transfer for the modal
  const selectedTransfer = pendingTransfers.find(
    (t) => t.transfer_id === selectedTransferId,
  ) ?? null;

  // Show the first pending transfer in the card (or null if none)
  const firstPending: import('@/types/collection.types').TransferPending | null =
    pendingTransfers.length > 0 ? pendingTransfers[0] ?? null : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Transferencias a UNE-Credito"
        subtitle="Gestion de transferencias"
        breadcrumbs={BREADCRUMBS}
        helpKey="transfers"
      />

      {/* Loading state */}
      {isLoading && (
        <div className="space-y-6">
          <Skeleton width="100%" height={280} variant="rectangular" />
          <Skeleton width="100%" height={300} variant="rectangular" />
        </div>
      )}

      {/* Error state */}
      {isError && !isLoading && (
        <div className="flex flex-col items-center gap-4 rounded-lg border border-destructive/20 bg-destructive/5 p-8 text-center">
          <p className="text-sm text-destructive">
            Error al cargar las transferencias. Intenta de nuevo.
          </p>
          <Button variant="outline" size="sm" onClick={refetchAll}>
            Reintentar
          </Button>
        </div>
      )}

      {/* Content */}
      {!isLoading && !isError && (
        <>
          <PendingTransferCard
            transfer={firstPending}
            isLoading={false}
            onApprove={openApproveModal}
          />

          <TransferHistoryTable
            transfers={transferHistory}
            isLoading={false}
          />

          <ApproveTransferModal
            isOpen={isApproveModalOpen}
            onConfirm={confirmApprove}
            onCancel={closeApproveModal}
            isApproving={isApproving}
            transfer={selectedTransfer}
          />
        </>
      )}
    </div>
  );
}
